(function() {
  const HERO_SELECTOR = '.wagmade-pinned-hero';
  const TITLE_SELECTOR = '[data-hero-3d-title]';
  const THREE_URL = 'https://esm.sh/three@0.165.0';
  const TEXT_GEOMETRY_URL = 'https://esm.sh/three@0.165.0/examples/jsm/geometries/TextGeometry.js';
  const FONT_LOADER_URL = 'https://esm.sh/three@0.165.0/examples/jsm/loaders/FontLoader.js';
  const FONT_URL = 'https://cdn.jsdelivr.net/npm/three@0.165.0/examples/fonts/helvetiker_regular.typeface.json';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (reduceMotion || !finePointer || window.innerWidth < 750) {
    return;
  }

  function canUseWebGL() {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
    } catch (error) {
      return false;
    }
  }

  function loadFont(FontLoader, data) {
    const loader = new FontLoader();
    return loader.parse(data);
  }

  function splitLines(text, maxWidth, fontSize, fontFamily) {
    const probe = document.createElement('canvas');
    const ctx = probe.getContext('2d');
    ctx.font = `300 ${fontSize}px ${fontFamily}`;
    const words = text.toUpperCase().split(/\s+/).filter(Boolean);
    const lines = [];
    let current = '';

    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (current && ctx.measureText(candidate).width > maxWidth) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    });

    if (current) lines.push(current);
    return lines.length ? lines : [text.toUpperCase()];
  }

  function clearObject(object) {
    object.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach((material) => material.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  }

  class Hero3DTitle {
    constructor(THREE, TextGeometry, font, hero, el) {
      this.THREE = THREE;
      this.TextGeometry = TextGeometry;
      this.font = font;
      this.hero = hero;
      this.el = el;
      this.h1 = el.querySelector('h1');
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'wagmade-hero-3d-title__canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.el.appendChild(this.canvas);

      this.pointer = { x: 0, y: 0 };
      this.current = { x: 0, y: 0 };
      this.frame = null;
      this.resizeRaf = null;
      this.isVisible = true;

      this.scene = new THREE.Scene();
      this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 2000);
      this.camera.position.set(0, 0, 900);

      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance'
      });
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
      if (THREE.SRGBColorSpace) {
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      }

      this.group = new THREE.Group();
      this.group.rotation.x = -0.04;
      this.scene.add(this.group);

      this.keyLight = new THREE.DirectionalLight(0xffffff, 4.8);
      this.keyLight.position.set(-180, 220, 420);
      this.scene.add(this.keyLight);

      this.rimLight = new THREE.DirectionalLight(0xdfe7ff, 2.8);
      this.rimLight.position.set(260, 80, 320);
      this.scene.add(this.rimLight);

      this.fillLight = new THREE.HemisphereLight(0xffffff, 0x303030, 2.2);
      this.scene.add(this.fillLight);

      this.material = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        emissive: 0xffffff,
        emissiveIntensity: 0.06,
        transparent: true,
        opacity: 0.82,
        roughness: 0.08,
        metalness: 0,
        transmission: 0.42,
        thickness: 1.35,
        ior: 1.42,
        clearcoat: 1,
        clearcoatRoughness: 0.035,
        reflectivity: 0.72,
        side: THREE.DoubleSide
      });

      this.shadowMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.12,
        depthWrite: false
      });

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onResize = this.onResize.bind(this);
      this.animate = this.animate.bind(this);

      hero.addEventListener('pointermove', this.onPointerMove, { passive: true });
      hero.addEventListener('pointerleave', this.onPointerLeave);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.el);

      this.build();
      this.el.dataset.webglReady = 'true';
      this.frame = requestAnimationFrame(this.animate);
    }

    onPointerMove(event) {
      const rect = this.hero.getBoundingClientRect();
      const nx = (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const ny = (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      this.pointer.x = Math.max(-1, Math.min(1, nx));
      this.pointer.y = Math.max(-1, Math.min(1, ny));
    }

    onPointerLeave() {
      this.pointer.x = 0;
      this.pointer.y = 0;
    }

    onResize() {
      if (this.resizeRaf) return;
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        this.build();
      });
    }

    build() {
      if (!this.h1 || !this.el.isConnected) return;

      while (this.group.children.length) {
        const child = this.group.children.pop();
        clearObject(child);
      }

      const canvasRect = this.canvas.getBoundingClientRect();
      const titleRect = this.el.getBoundingClientRect();
      const h1Style = window.getComputedStyle(this.h1);
      const width = Math.max(1, Math.round(canvasRect.width));
      const height = Math.max(1, Math.round(canvasRect.height));

      this.renderer.setSize(width, height, false);
      this.camera.left = -width / 2;
      this.camera.right = width / 2;
      this.camera.top = height / 2;
      this.camera.bottom = -height / 2;
      this.camera.updateProjectionMatrix();

      const sourceText = this.el.dataset.text || this.h1.textContent || '';
      const fontSize = parseFloat(h1Style.fontSize) || 96;
      const lineHeight = parseFloat(h1Style.lineHeight) || fontSize * 0.94;
      const size = fontSize * 0.78;
      const maxTextWidth = Math.max(220, titleRect.width);
      const lines = splitLines(sourceText, maxTextWidth, fontSize, h1Style.fontFamily || 'Arial');
      const titleLeft = titleRect.left - canvasRect.left;
      const titleTop = titleRect.top - canvasRect.top;
      const startX = -width / 2 + titleLeft;
      const startY = height / 2 - titleTop - fontSize * 0.83;

      lines.forEach((line, index) => {
        const geometry = new this.TextGeometry(line, {
          font: this.font,
          size,
          depth: fontSize * 0.22,
          curveSegments: 18,
          bevelEnabled: true,
          bevelThickness: fontSize * 0.025,
          bevelSize: fontSize * 0.018,
          bevelSegments: 5
        });
        geometry.computeBoundingBox();
        geometry.computeVertexNormals();

        const mesh = new this.THREE.Mesh(geometry, this.material);
        mesh.position.set(startX, startY - index * lineHeight, 0);
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        this.group.add(mesh);

        const glow = new this.THREE.Mesh(geometry.clone(), this.shadowMaterial);
        glow.position.set(startX + fontSize * 0.035, startY - index * lineHeight - fontSize * 0.035, -fontSize * 0.22);
        glow.scale.set(1.006, 1.006, 1.006);
        this.group.add(glow);
      });

      this.render();
    }

    animate() {
      this.current.x += (this.pointer.x - this.current.x) * 0.075;
      this.current.y += (this.pointer.y - this.current.y) * 0.075;

      this.group.rotation.y = this.current.x * 0.18;
      this.group.rotation.x = -0.04 - this.current.y * 0.12;
      this.group.position.x = this.current.x * 5;
      this.group.position.y = -this.current.y * 4;
      this.keyLight.position.x = -180 + this.current.x * 160;
      this.keyLight.position.y = 220 - this.current.y * 90;

      this.render();
      this.frame = requestAnimationFrame(this.animate);
    }

    render() {
      this.renderer.render(this.scene, this.camera);
    }
  }

  async function init() {
    if (!canUseWebGL()) return;

    const hero = document.querySelector(HERO_SELECTOR);
    const titles = Array.from(document.querySelectorAll(TITLE_SELECTOR));
    if (!hero || titles.length === 0) return;

    try {
      const [THREE, textModule, loaderModule, fontData] = await Promise.all([
        import(THREE_URL),
        import(TEXT_GEOMETRY_URL),
        import(FONT_LOADER_URL),
        fetch(FONT_URL, { mode: 'cors' }).then((response) => response.json())
      ]);
      const font = loadFont(loaderModule.FontLoader, fontData);
      titles.forEach((title) => new Hero3DTitle(THREE, textModule.TextGeometry, font, hero, title));
    } catch (error) {
      hero.dataset.hero3dTextFallback = 'true';
      console.warn('Wagmade hero 3D text fallback:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
